// ═══════════════════════════════════════════════════════════════
// RollCall — Mobile Check-In
// PIN entry with GPS, check-in/out toggle
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Select, Modal, Stack,
} from '../../components/gab/GabComponents';
import {
  IconKeyboard, IconMapPin, IconLogin, IconLogout,
  IconClock,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { useRollCallStore } from '../../store/rollcallStore';
import { primary, semantic } from '../../lib/colors';

export default function CheckIn() {
  const { user, incidents, loadIncidents } = useAuthStore();
  const {
    events, checkins, personnel,
    loadEvents, loadCheckins, loadPersonnel,
    checkIn, checkOut, findByPin,
  } = useRollCallStore();

  const [pin, setPin] = useState('');
  const [geoLocation, setGeoLocation] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<{
    type: 'checkin' | 'checkout'; name: string; agency: string; time: Date;
  } | null>(null);
  const [checkoutModal, setCheckoutModal] = useState<{
    person: { firstName: string; lastName: string; agency: string; id: number };
    checkinId: number; checkInTime: string;
  } | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadIncidents(); loadEvents(); loadCheckins(); loadPersonnel();
  }, []);

  // GPS
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGeoLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        err => setGeoError(err.message),
        { enableHighAccuracy: true }
      );
    }
  }, []);

  const openEvents = events.filter(e => e.eventStatus === 'open');
  const selectedEvent = openEvents.find(e => String(e.id) === selectedEventId);
  const incidentName = selectedEvent
    ? incidents.find(i => String(i.id) === String(selectedEvent.incidentId))?.name || ''
    : '';

  const handlePinSubmit = useCallback(async () => {
    if (!pin.trim() || !selectedEventId) {
      notifications.show({ message: 'Enter PIN and select an event', color: 'yellow' });
      return;
    }
    const person = findByPin(pin);
    if (!person) {
      notifications.show({ message: 'Invalid PIN — Person not found', color: 'red' });
      setPin('');
      return;
    }

    // Check if already checked in
    const activeCheckin = checkins.find(
      c => String(c.eventId) === selectedEventId &&
           String(c.personnelId) === String(person.id) &&
           !c.checkOutTime
    );

    if (activeCheckin) {
      setCheckoutModal({
        person, checkinId: activeCheckin.id, checkInTime: activeCheckin.checkInTime,
      });
      setPin('');
      return;
    }

    // Check in
    try {
      await checkIn({
        eventId: selectedEventId,
        personnelId: String(person.id),
        personnelName: `${person.firstName} ${person.lastName}`,
        agency: person.agency,
        position: person.position,
        method: 'mobile',
        geoLat: geoLocation ? String(geoLocation.lat) : '',
        geoLng: geoLocation ? String(geoLocation.lng) : '',
        geoAccuracy: geoLocation ? String(geoLocation.accuracy) : '',
      });
      setLastAction({ type: 'checkin', name: `${person.firstName} ${person.lastName}`, agency: person.agency, time: new Date() });
      notifications.show({ message: `${person.firstName} ${person.lastName} checked in`, color: 'green' });
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Check-in failed', color: 'red' });
    }
    setPin('');
  }, [pin, selectedEventId, checkins, findByPin, checkIn, geoLocation]);

  const handleConfirmCheckout = async () => {
    if (!checkoutModal) return;
    setProcessing(true);
    try {
      await checkOut(checkoutModal.checkinId, user?.fullName ?? 'system');
      setLastAction({
        type: 'checkout',
        name: `${checkoutModal.person.firstName} ${checkoutModal.person.lastName}`,
        agency: checkoutModal.person.agency,
        time: new Date(),
      });
      notifications.show({
        message: `${checkoutModal.person.firstName} ${checkoutModal.person.lastName} checked out`,
        color: 'blue',
      });
      setCheckoutModal(null);
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Check-out failed', color: 'red' });
    } finally {
      setProcessing(false);
    }
  };

  const handleKeyPress = (digit: string) => {
    if (digit === 'clear') { setPin(''); return; }
    if (digit === 'submit') { handlePinSubmit(); return; }
    if (pin.length < 6) setPin(prev => prev + digit);
  };

  return (
    <div>
      <Title order={3} mb="md" c="black">Mobile Check-In</Title>

      {/* GPS Status */}
      <Paper p="sm" radius="sm" withBorder mb="md">
        <Group gap="xs">
          <IconMapPin size={14} style={{ color: geoLocation ? '#07963F' : semantic.error }} />
          <Text size="xs" c="dimmed">
            {geoLocation
              ? `GPS: ${geoLocation.lat.toFixed(5)}, ${geoLocation.lng.toFixed(5)} (±${Math.round(geoLocation.accuracy)}m)`
              : geoError ? `GPS Error: ${geoError}` : 'Acquiring GPS...'}
          </Text>
        </Group>
      </Paper>

      {/* Event Selection */}
      <Select label="Active Event" placeholder="Select an event" size="md"
        data={openEvents.map(e => ({ value: String(e.id), label: `${e.eventName} — ${e.eventDate || 'No date'}` }))}
        value={selectedEventId} onChange={setSelectedEventId} mb="md" />

      {selectedEventId && selectedEvent && (
        <Paper p="sm" radius="sm" withBorder mb="md" style={{ background: 'rgba(63,114,175,0.05)' }}>
          <Text size="sm" fw={600}>{selectedEvent.name}</Text>
          <Text size="xs" c="dimmed">{incidentName} · {selectedEvent.eventDate}</Text>
          <Text size="xs" c="dimmed" mt={2}>Enter PIN — check in or check out if already in.</Text>
        </Paper>
      )}

      {/* PIN Pad */}
      <Paper p="lg" radius="sm" withBorder maw={320} mx="auto"
        style={{ background: '#112D4E' }}>
        <Text ta="center" c="white" fw={600} mb="sm">
          <IconKeyboard size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Enter PIN
        </Text>

        {/* PIN dots */}
        <Group justify="center" mb="md" gap="xs">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{
              width: 14, height: 14, borderRadius: '50%',
              background: i < pin.length ? '#07963F' : 'rgba(255,255,255,0.2)',
              transition: 'background 0.15s',
            }} />
          ))}
        </Group>

        {/* Keypad */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, maxWidth: 240, margin: '0 auto' }}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'submit'].map(key => (
            <Button key={key} variant={key === 'submit' ? 'filled' : key === 'clear' ? 'subtle' : 'light'}
              color={key === 'submit' ? 'green' : key === 'clear' ? 'gray' : 'blue'}
              size="lg" onClick={() => handleKeyPress(key)}
              style={{ fontWeight: 700, fontSize: key === 'clear' || key === 'submit' ? 11 : 18 }}>
              {key === 'clear' ? 'CLR' : key === 'submit' ? 'GO' : key}
            </Button>
          ))}
        </div>
      </Paper>

      {/* Last Action */}
      {lastAction && (
        <Paper p="md" radius="sm" withBorder mt="md" style={{
          borderLeft: `3px solid ${lastAction.type === 'checkin' ? '#07963F' : primary[700]}`,
        }}>
          <Group justify="space-between">
            <div>
              <Text size="sm" fw={700} style={{ color: lastAction.type === 'checkin' ? '#07963F' : primary[700] }}>
                {lastAction.type === 'checkin' ? (
                  <><IconLogin size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Checked In</>
                ) : (
                  <><IconLogout size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Checked Out</>
                )}
              </Text>
              <Text size="lg" fw={700}>{lastAction.name}</Text>
              <Text size="xs" c="dimmed">{lastAction.agency} · {lastAction.time.toLocaleTimeString()}</Text>
            </div>
            {geoLocation && (
              <Badge color="blue" size="sm" leftSection={<IconMapPin size={10} />}>GPS</Badge>
            )}
          </Group>
        </Paper>
      )}

      {/* Admin Check-in by Name */}
      {selectedEventId && (
        <Paper p="sm" radius="sm" withBorder mt="md">
          <Text fw={600} size="sm" mb="xs">Admin: Check In/Out by Name</Text>
          <Select
            placeholder="Search personnel by name..."
            searchable
            data={personnel.filter(p => p.status === 'active').map(p => ({
              value: String(p.id),
              label: `${p.firstName} ${p.lastName}${p.agency ? ` — ${p.agency}` : ''}`,
            }))}
            onChange={async (val) => {
              if (!val || !selectedEventId) return;
              const person = personnel.find(p => String(p.id) === val);
              if (!person) return;
              const activeCheckin = checkins.find(c =>
                String(c.personnelId) === val &&
                String(c.eventId) === selectedEventId &&
                !c.checkOutTime
              );
              if (activeCheckin) {
                await checkOut(activeCheckin.id, user?.fullName || user?.email || 'Admin');
                notifications.show({ color: 'blue', title: 'Checked Out', message: `${person.firstName} ${person.lastName} checked out` });
                setLastAction({ type: 'checkout', name: `${person.firstName} ${person.lastName}`, agency: person.agency || '', time: new Date() });
              } else {
                await checkIn({
                  eventId: selectedEventId,
                  personnelId: String(person.id),
                  personnelName: `${person.firstName} ${person.lastName}`,
                  agency: person.agency || '',
                  position: person.position || '',
                  method: 'admin',
                  geoLat: geoLocation ? String(geoLocation.lat) : undefined,
                  geoLng: geoLocation ? String(geoLocation.lng) : undefined,
                });
                notifications.show({ color: 'green', title: 'Checked In', message: `${person.firstName} ${person.lastName} checked in` });
                setLastAction({ type: 'checkin', name: `${person.firstName} ${person.lastName}`, agency: person.agency || '', time: new Date() });
              }
              // checkIn/checkOut already refreshes checkins internally — no extra fetch needed
            }}
            size="sm"
            nothingFoundMessage="No personnel found"
          />
          <Text size="xs" c="dimmed" mt={4}>Select a person to toggle their check-in status for the active event.</Text>
        </Paper>
      )}

      {/* Checkout Confirm Modal */}
      <Modal opened={!!checkoutModal} onClose={() => setCheckoutModal(null)}
        title="Confirm Check Out" centered size="sm">
        {checkoutModal && (
          <Stack align="center" gap="sm">
            <IconLogout size={36} style={{ color: primary[700] }} />
            <Text size="lg" fw={700}>
              {checkoutModal.person.firstName} {checkoutModal.person.lastName}
            </Text>
            <Text size="sm" c="dimmed">{checkoutModal.person.agency}</Text>
            {checkoutModal.checkInTime && (
              <Paper p="sm" radius="sm" style={{ background: 'var(--mantine-color-blue-0)', width: '100%' }}>
                <Group gap="xs" justify="center">
                  <IconClock size={14} />
                  <Text size="sm">Checked in at <strong>{new Date(checkoutModal.checkInTime).toLocaleTimeString()}</strong></Text>
                </Group>
              </Paper>
            )}
            <Group justify="center" gap="md" mt="sm">
              <Button variant="light" onClick={() => setCheckoutModal(null)}>Cancel</Button>
              <Button color="blue" onClick={handleConfirmCheckout} loading={processing}
                leftSection={<IconLogout size={14} />}>Check Out</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
